import { recognizeSchema } from "@redotech/redo-flows-common/schemas/recognize";
import { ConditionDataSource } from "@redotech/redo-flows-common/steps";
import { useTranslation } from "@redotech/redo-i18n";
import BranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { memo } from "react";
import { BaseNode } from "./base-node";
import type { BranchNodeData } from "./branch-node";
import { useFlipBranchesMenuItems } from "./use-flip-branches-menu-items";

interface RecognizeConditionNodeProps {
  id: string;
  data: BranchNodeData;
  selected?: boolean;
}

export const RecognizeConditionNode = memo(function RecognizeConditionNode({
  id,
  data,
  selected,
}: RecognizeConditionNodeProps) {
  const { t } = useTranslation();
  const menuItems = useFlipBranchesMenuItems({
    id,
    step: data.step,
    readonly: data.readonly,
    updateStep: data.updateStep,
  });
  const expression = data.step.expression;
  let summary = t("Choose a {{productName}} criterion", {
    productName: "Recognize",
  });

  if (expression?.dataSource === ConditionDataSource.TriggerData) {
    const condition = expression.schemaBooleanExpression;
    const field = Object.entries(recognizeSchema.fields).find(
      ([key]) => key === condition?.field,
    );

    if (field && condition?.type === "boolean_evaluation") {
      summary =
        condition.value === false
          ? t("If {{fieldLabel}}: No", { fieldLabel: field[1].label })
          : t("If {{fieldLabel}}: Yes", { fieldLabel: field[1].label });
    }
  }

  let onDelete: (() => void) | undefined;

  if (!data.readonly) {
    onDelete = () => data.deleteElements(id);
  }

  return (
    <BaseNode
      additionalMenuItems={menuItems}
      icon={<RedoFeaturedIcon color="warning" Icon={BranchIcon} size="xs" />}
      onDelete={onDelete}
      selected={selected}
      step={data.step}
      title={t("Condition")}
    >
      <Handle position={Position.Top} type="target" />
      <Text fontSize="xxs">{summary}</Text>
      <Handle id="a" position={Position.Bottom} type="source" />
      <Handle id="b" position={Position.Bottom} type="source" />
    </BaseNode>
  );
});
